import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLingui } from '@lingui/react';
import { t } from '@lingui/core/macro';
import { useDebouncedValue } from '@/common/hooks/useDebouncedValue';
import { useConfirmDialog } from '@/common/components/confirm-dialog/useConfirmDialog';
import { GEOLOCATION_CONFIG } from '@/features/geolocation/config';
import { geoQueries } from '@/features/geolocation/queries';
import { toGeocodeResult } from '@/features/geolocation/mappers/toGeocodeResult';
import type {
  AddressInput,
  GeoCoordinates,
  LocationValue,
} from '@/features/geolocation/types/location';
import {
  addressCacheKey,
  classifyPrecision,
  resolveApplicableTiers,
} from '@/features/geolocation/utils/geocodeQuery';
import { initialPickerState, locationPickerReducer } from './locationPickerReducer';

export interface UseLocationPickerArgs {
  value: LocationValue;
  onChange: (next: LocationValue) => void;
  address: AddressInput;
  /** Nonce; increment on address-field blur to arm a debounced auto-locate. */
  locateSignal: number;
}

/** Placeholder for disabled coordinate queries — never fetched, keys stay stable. */
const NO_COORDINATES: GeoCoordinates = { latitude: 0, longitude: 0 };

function roundToTwo(value: number): number {
  return Math.round(value * 100) / 100;
}

export function useLocationPicker({
  value,
  onChange,
  address,
  locateSignal,
}: UseLocationPickerArgs) {
  const { i18n } = useLingui();
  const confirm = useConfirmDialog();
  const language = i18n.locale;

  const { street, city, postalcode, country } = address;
  // Host forms rebuild the address object on every keystroke; key the memo on the
  // primitives so query keys and debounced payloads stay referentially stable.
  const stableAddress = useMemo<AddressInput>(
    () => ({ street, city, postalcode, country }),
    [street, city, postalcode, country]
  );

  const addressKey = addressCacheKey('full', stableAddress);
  const tiers = useMemo(() => resolveApplicableTiers(stableAddress), [stableAddress]);

  const [state, dispatch] = useReducer(
    locationPickerReducer,
    value.latitude !== null,
    initialPickerState
  );

  /** Address the pinned position belongs to, so we can spot a later address edit. */
  const [pinnedAddressKey, setPinnedAddressKey] = useState<string | null>(
    value.latitude !== null ? addressKey : null
  );

  // ─── Debounced auto-locate on address blur ──────────────────────────────────

  // Debounced on its own — NOT bundled with addressKey/tiers/stableAddress.
  // Those change on every keystroke as the host form rebuilds `address` live,
  // and bundling them into one debounced value would reset the debounce timer
  // on every keystroke, firing ~500 ms after the user stops typing instead of
  // on blur. `locateSignal` only changes when a host field's onBlur fires, so
  // debouncing it alone is what actually coalesces a tab-through without ever
  // triggering on a keystroke.
  const debouncedLocateSignal = useDebouncedValue(
    locateSignal,
    GEOLOCATION_CONFIG.debounce.autoLocateMs
  );

  useEffect(() => {
    // Nonce 0 is the initial render — a form must never geocode just by opening.
    if (debouncedLocateSignal === 0) return;

    dispatch({
      type: 'AUTO_LOCATE_REQUESTED',
      addressKey,
      address: stableAddress,
      tiers,
    });
    // Intentionally reacting to the debounced signal alone — addressKey/
    // stableAddress/tiers are read fresh here from the latest render, not from
    // whichever render happened to schedule this debounce.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedLocateSignal]);

  // ─── The tier chain ─────────────────────────────────────────────────────────

  // One query; the key changes as the reducer advances the tier, so every tier is
  // cached independently and an already-cached empty tier advances with no network.
  const isLocating = state.status === 'locating' && state.tier !== null;
  const geocodeQuery = useQuery({
    ...geoQueries.geocode(state.tier ?? 'full', state.attemptAddress ?? stableAddress, language),
    enabled: isLocating && state.attemptAddress !== null,
  });

  const { data: geocodeData, isError: isGeocodeError } = geocodeQuery;

  useEffect(() => {
    if (state.status !== 'locating' || state.tier === null) return;

    if (isGeocodeError) {
      dispatch({ type: 'TIER_ERROR' });
      return;
    }

    if (!geocodeData) return;

    const place = geocodeData[0];
    const result = place ? toGeocodeResult(place) : null;

    if (!result) {
      dispatch({ type: 'TIER_MISS' });
      return;
    }

    dispatch({
      type: 'TIER_RESULT',
      result,
      precision: classifyPrecision(result, state.tier),
    });
    onChange({
      latitude: result.coordinates.latitude,
      longitude: result.coordinates.longitude,
      altitude: null,
      source: 'geocoded',
      altitudeSource: null,
    });
  }, [geocodeData, isGeocodeError, state.status, state.tier, onChange]);

  // ─── Coordinates -> altitude ────────────────────────────────────────────────

  const { latitude, longitude } = value;
  const coordinates = useMemo<GeoCoordinates | null>(
    () => (latitude !== null && longitude !== null ? { latitude, longitude } : null),
    [latitude, longitude]
  );

  // Only the SETTLED coordinate becomes a query key, so a burst of map clicks
  // cannot queue N rate-limited requests.
  const debouncedCoordinates = useDebouncedValue(
    coordinates,
    GEOLOCATION_CONFIG.debounce.coordinatesMs
  );

  // `useDebouncedValue` hands back the very object it was given, and `coordinates`
  // is memoised on the two numbers — so reference equality is an exact test for
  // "the debounced coordinate is still the one in the form". Without it, a lagging
  // debounced value would attach the previous position's altitude to the new one.
  const settledCoordinates = debouncedCoordinates === coordinates ? coordinates : null;

  const isAltitudeManual = value.altitudeSource === 'manual';
  const elevationQuery = useQuery({
    ...geoQueries.elevation(settledCoordinates ?? NO_COORDINATES),
    // Only ever fetched to FILL IN a missing altitude: opening a form on a stored
    // position must not overwrite it, and must not spend the provider's quota.
    // Every path that moves the position resets the altitude to null, which
    // re-arms this.
    enabled: settledCoordinates !== null && !isAltitudeManual && value.altitude === null,
  });

  const { data: elevationData, refetch: refetchElevation } = elevationQuery;

  useEffect(() => {
    if (!elevationData || isAltitudeManual) return;

    const metres = roundToTwo(elevationData.metres);
    if (value.altitude === metres && value.altitudeSource === 'auto') return;

    onChange({ ...value, altitude: metres, altitudeSource: 'auto' });
  }, [elevationData, isAltitudeManual, value, onChange]);

  // ─── Actions ────────────────────────────────────────────────────────────────

  const onMapPick = useCallback(
    (picked: GeoCoordinates) => {
      setPinnedAddressKey(addressKey);
      dispatch({ type: 'MAP_PICKED' });
      onChange({
        ...value,
        latitude: picked.latitude,
        longitude: picked.longitude,
        // Altitude follows the new position unless the user typed one.
        altitude: value.altitudeSource === 'manual' ? value.altitude : null,
        source: 'manual',
      });
    },
    [addressKey, onChange, value]
  );

  const onCoordinatesEdit = useCallback(
    (next: { latitude?: number | null; longitude?: number | null }) => {
      setPinnedAddressKey(addressKey);
      dispatch({ type: 'COORDS_EDITED' });
      onChange({
        ...value,
        latitude: next.latitude !== undefined ? next.latitude : value.latitude,
        longitude: next.longitude !== undefined ? next.longitude : value.longitude,
        altitude: value.altitudeSource === 'manual' ? value.altitude : null,
        source: 'manual',
      });
    },
    [addressKey, onChange, value]
  );

  const onAltitudeEdit = useCallback(
    (altitude: number | null) => {
      onChange({ ...value, altitude, altitudeSource: 'manual' });
    },
    [onChange, value]
  );

  const onAltitudeAuto = useCallback(() => {
    onChange({ ...value, altitude: null, altitudeSource: null });
  }, [onChange, value]);

  const startLocate = useCallback(() => {
    dispatch({ type: 'MANUAL_LOCATE_REQUESTED', addressKey, address: stableAddress, tiers });
  }, [addressKey, stableAddress, tiers]);

  const onLocateClick = useCallback(async () => {
    // A position the user marked themselves is never replaced silently.
    if (state.status === 'manual') {
      const confirmed = await confirm({
        title: t`Replace the position you marked?`,
        description: t`The position you marked on the map will be replaced by the result of an address lookup.`,
        confirmLabel: t`Replace position`,
      });
      if (!confirmed) return;
    }

    startLocate();
  }, [confirm, startLocate, state.status]);

  const onClear = useCallback(() => {
    setPinnedAddressKey(null);
    dispatch({ type: 'CLEARED' });
    onChange({
      latitude: null,
      longitude: null,
      altitude: null,
      // Not `null`: the payload mapper distinguishes "the user cleared an existing
      // position" (write nulls) from "never touched" (omit the keys entirely).
      source: 'manual',
      altitudeSource: null,
    });
  }, [onChange]);

  const retryElevation = useCallback(() => {
    void refetchElevation();
  }, [refetchElevation]);

  // ─── Derived UI state ───────────────────────────────────────────────────────

  return {
    status: state.status,
    precision: state.precision,
    resolvedTier: state.resolvedTier,
    // Never populated for a manual pick — the user already typed the address
    // themselves, so a reverse-geocoded label would be redundant, and fetching
    // one on every click reflowed the status area and jostled the map underneath
    // the user's cursor.
    displayName: state.displayName,
    isPinned: state.isPinned,
    addressChangedWhilePinned:
      state.isPinned && pinnedAddressKey !== null && pinnedAddressKey !== addressKey,
    coordinates,
    bounds: state.bounds,
    viewportNonce: state.viewportNonce,
    canLocate: tiers.length > 0,
    isLocating,
    /** Tier in flight, so the status line can say "trying a wider search". */
    tier: state.tier,
    hasPosition: coordinates !== null,
    elevation: {
      isPending: elevationQuery.isFetching,
      isError: elevationQuery.isError,
      isSuspect: elevationData?.isSuspect === true,
      retry: retryElevation,
    },
    onMapPick,
    onCoordinatesEdit,
    onAltitudeEdit,
    onAltitudeAuto,
    onLocateClick,
    onClear,
  };
}

export type LocationPickerApi = ReturnType<typeof useLocationPicker>;
